import { useState } from "react";
import { ChevronDownIcon, StarIcon } from "lucide-react";

import { ResumeActionsMenu } from "./ResumeActionsMenu";

import { Column, DocumentPreview, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useErrorToast } from "@/hooks/useErrorToast";
import { useUpdateProfileUser } from "@/hooks/mutations/useUpdateProfileUser";
import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { ResumeFileType } from "@/types";

const ENRICHED_RESUME_TITLE_MARKER = "Laborup Resume";

export const ResumeListDropdown = ({
  onPrimaryResumeChanged,
  onResumeUpdated,
  primaryResumeId,
  resumeFiles,
  totalCount,
  userId,
}: {
  onPrimaryResumeChanged: (resumeFileId: number) => void;
  onResumeUpdated: () => void;
  primaryResumeId: number | null;
  resumeFiles: ResumeFileType[];
  totalCount: number;
  userId: number;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: updateUser } = useUpdateProfileUser();

  /* React Hooks */
  const errorToast = useErrorToast();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [previewResume, setPreviewResume] = useState<ResumeFileType>();

  /* Handlers */
  const handleSetPrimary = (resumeFile: ResumeFileType) => {
    onPrimaryResumeChanged(resumeFile.id);
    updateUser(
      {
        data: { primaryResumeId: resumeFile.id },
        userId,
      },
      {
        onSuccess: onResumeUpdated,
        onError: () => {
          errorToast({ description: "Failed to set primary resume." });
        },
      },
    );
  };

  /* Render */
  const renderResume = (resumeFile: ResumeFileType) => {
    const uploadedAt = (resumeFile as { createdAt?: string }).createdAt;
    const uploadedLabel = uploadedAt
      ? // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        dates.formatIsoDateForUser(uploadedAt) ||
        dates.timeAgoForJob(uploadedAt)
      : null;
    const isEnriched = (resumeFile.title || "").includes(
      ENRICHED_RESUME_TITLE_MARKER,
    );

    return (
      <Row
        className="min-w-0 cursor-pointer items-center gap-3 rounded-lg p-2 hover:bg-slate-50"
        key={resumeFile.id}
        onClick={() => {
          setIsDropdownOpen(false);
          setPreviewResume(resumeFile);
        }}
      >
        <Column className="relative h-16 w-12 shrink-0 overflow-hidden rounded border border-slate-200 bg-slate-50">
          <div className="pointer-events-none absolute left-1/2 top-1 w-[520px] origin-top -translate-x-1/2 scale-[0.1] opacity-70">
            <DocumentPreview href={resumeFile.url ?? ""} pageWidthScale={1} />
          </div>
        </Column>
        <Column className="min-w-0 flex-1 gap-1.5">
          <Text className="truncate text-sm font-medium text-slate-800">
            {resumeFile.title || "Resume"}
          </Text>
          <Row className="items-center gap-2">
            {uploadedLabel ? (
              <Text className="text-xs font-normal text-slate-400">
                {uploadedLabel}
              </Text>
            ) : null}
            <Text
              className={cn(
                "rounded-md px-2 py-0.5 text-xs font-medium",
                isEnriched
                  ? "bg-green-50 text-green-700"
                  : "bg-slate-100 text-slate-600",
              )}
            >
              {isEnriched ? "Enriched" : "Uploaded"}
            </Text>
          </Row>
        </Column>
        <Row
          className="shrink-0 items-center gap-1"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <Button
            className={cn(
              "h-8 w-8 text-slate-400 hover:bg-slate-100 hover:text-yellow-500",
              resumeFile.id === primaryResumeId &&
                "text-yellow-500 hover:text-yellow-500",
            )}
            disabled={resumeFile.id === primaryResumeId}
            onClick={(event) => {
              event.stopPropagation();
              handleSetPrimary(resumeFile);
            }}
            size="icon"
            title="Set primary resume"
            type="button"
            variant="ghost"
          >
            <StarIcon
              className={cn(
                "h-4 w-4",
                resumeFile.id === primaryResumeId && "fill-yellow-500",
              )}
            />
          </Button>
          <ResumeActionsMenu
            onResumeUpdated={onResumeUpdated}
            primaryResumeId={primaryResumeId}
            resumeFile={resumeFile}
            userId={userId}
          />
        </Row>
      </Row>
    );
  };

  const render = () => {
    return (
      <>
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <DropdownMenu
              onOpenChange={setIsDropdownOpen}
              open={isDropdownOpen}
            >
              <DropdownMenuTrigger asChild>
                <TooltipTrigger asChild>
                  <Button
                    className="h-auto gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 hover:bg-slate-200"
                    type="button"
                    variant="ghost"
                  >
                    {totalCount}
                    <ChevronDownIcon
                      className="h-3.5 w-3.5"
                      strokeWidth={1.5}
                    />
                  </Button>
                </TooltipTrigger>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="max-h-[60vh] w-96 space-y-1 overflow-y-auto overscroll-contain p-2"
              >
                {resumeFiles.map(renderResume)}
              </DropdownMenuContent>
            </DropdownMenu>
            <TooltipContent side="top">
              <Text className="text-xs font-medium text-slate-900">
                See all
              </Text>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Dialog
          onOpenChange={(isOpen) => {
            if (!isOpen) setPreviewResume(undefined);
          }}
          open={!!previewResume}
        >
          <DialogContent className="flex h-[92vh] max-w-5xl flex-col gap-0 overflow-hidden p-0">
            <DialogTitle className="sr-only">Resume preview</DialogTitle>
            <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50 p-4">
              <DocumentPreview
                centered
                href={previewResume?.url ?? ""}
                pageGap={12}
              />
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  };

  return render();
};
